import React from 'react';
import VisuallyHidden from '@reach/visually-hidden';
import { ErrorState } from '../../BeFi20/components/ErrorState';
import { PulseCheckResults, useResultsQuery } from '../api/useResultsQuery';
import { Wrapper } from './Results.styles';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Hero } from './components/Hero/Hero';
import { Category } from './components/Category/Category';
import { Loading } from '../../CommonComponents/OPL/Loading/Loading';
import { Alert } from '../../CommonComponents/OPL/Alert/Alert';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { PulseCheckVerticalLogo } from '../img/PulseCheckVerticalLogo';

interface Props {
  clientId: string;
}

export const Results = ({ clientId }: Props) => {
  const { isLoading, isError, refetch, data } = useResultsQuery({ clientId });

  if (isLoading) {
    return (
      <Box display="grid" flex={1} css="place-content: center">
        <Loading text="Loading..." />
      </Box>
    );
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    throw new Error('No data in PulseCheck workflow');
  }

  return <ResultsContent clientId={clientId} data={data} />;
};

interface ContentProps {
  clientId: string;
  data: PulseCheckResults;
}

const ResultsContent = ({ clientId, data: { categories } }: ContentProps) => {
  const canAddGoals =
    categories.flatMap(({ goals }) => goals.filter(({ isComplete }) => !isComplete)).length < 3;

  return (
    <Wrapper initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <VisuallyHidden as="h1">Pulse Check Results</VisuallyHidden>
      <Box display="flex" flexDirection="column" alignItems="center" width={640}>
        <PulseCheckVerticalLogo />
      </Box>

      <Box boxShadow="400" borderRadius="xxl" width={640} my="40" css="overflow: hidden" bg="bg">
        <Hero categories={categories.slice(0, 3)} />

        {!canAddGoals && (
          <Box p="16" borderColor="neutral.200" borderWidth="0px 0px 1px 0px" borderStyle="solid">
            <Alert
              variant="warning"
              message="You've reached the maximum limit of three. This indicates that you may need to complete or remove existing goals if you wish to set new ones."
            />
          </Box>
        )}

        {categories.map(category => (
          <Category
            key={category.id}
            clientId={clientId}
            category={category}
            canAddGoals={canAddGoals}
          />
        ))}

        <Box display="flex" flexDirection="column" p="24">
          <Button
            variant="secondary"
            leftIcon="far fa-redo"
            onClick={() => {
              ADV.util.Router.go2('PulseCheckWorkflow', {
                clientId,
              });
            }}
          >
            Retake Assessment
          </Button>
        </Box>
      </Box>
    </Wrapper>
  );
};
